import { Search, UserPlus, Users, FileText, Bell, Crown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { MembersPanel, MembershipRevenueReport } from "@/components/Membership";
import { SmsDelivery } from "@/components/SmsDelivery";
import { useAuth } from "@/lib/shared";

export const RevenueSnapshot = ({ revenue, onJump }) => {
  const r = revenue || {};
  const collected = r.collected || 0;
  const outstanding = r.outstanding || 0;
  const services = r.services || 0;
  const delivery = r.delivery_fees || 0;
  const checkouts = r.checkouts || 0;
  const byMethod = r.by_method || {};
  const grandTotal = collected + outstanding;

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  const fmt = (n) => `$${(n || 0).toFixed(2)}`;

  return (
    <Card className="gold-border bg-card/70 overflow-hidden" data-testid="revenue-snapshot">
      <CardContent className="p-0">
        <div className="relative p-5 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-transparent">
          <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-radial from-amber-500/20 to-transparent rounded-full blur-2xl pointer-events-none" />
          <div className="relative flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-primary/80">Today's Revenue</p>
              <p className="text-xs text-muted-foreground mt-0.5">{today}</p>
              <p className="text-4xl font-bold gold-gradient-text brand-heading mt-2" data-testid="revenue-collected">{fmt(collected)}</p>
              <p className="text-xs text-muted-foreground mt-1">collected from {checkouts} checkout{checkouts === 1 ? '' : 's'}</p>
            </div>
            <button type="button" onClick={() => onJump?.('calendar')} className="text-right group cursor-pointer" data-testid="revenue-outstanding">
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-rose-400/80">Outstanding (Bill Later)</p>
              <p className={`text-2xl font-bold mt-2 transition-colors ${outstanding > 0 ? 'text-rose-400 group-hover:text-rose-300' : 'text-muted-foreground'}`}>{fmt(outstanding)}</p>
              {outstanding > 0 && <p className="text-[10px] text-muted-foreground mt-1 group-hover:text-primary transition-colors">Click to view calendar →</p>}
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-5">
            <div className="bg-background/40 backdrop-blur rounded-lg p-3 border border-border/40">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Services</p>
              <p className="text-lg font-bold text-foreground mt-0.5" data-testid="revenue-services">{fmt(services)}</p>
            </div>
            <div className="bg-background/40 backdrop-blur rounded-lg p-3 border border-border/40">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Delivery Fees</p>
              <p className="text-lg font-bold text-foreground mt-0.5" data-testid="revenue-delivery">{fmt(delivery)}</p>
            </div>
            <div className="bg-background/40 backdrop-blur rounded-lg p-3 border border-border/40">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Day Total</p>
              <p className="text-lg font-bold text-primary mt-0.5">{fmt(grandTotal)}</p>
            </div>
            <div className="bg-background/40 backdrop-blur rounded-lg p-3 border border-border/40">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Avg / Checkout</p>
              <p className="text-lg font-bold text-foreground mt-0.5">{fmt(checkouts > 0 ? grandTotal / checkouts : 0)}</p>
            </div>
          </div>

          {checkouts > 0 && (collected > 0 || outstanding > 0) && (
            <div className="mt-4 pt-3 border-t border-primary/20">
              <p className="text-[9px] text-muted-foreground uppercase tracking-widest mb-2">Payment Methods</p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { key: 'card', label: 'Card', color: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
                  { key: 'cash', label: 'Cash', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
                  { key: 'external', label: 'Venmo / Cash App', color: 'bg-violet-500/15 text-violet-300 border-violet-500/30' },
                  { key: 'paid_ahead', label: 'Pre-Paid', color: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
                  { key: 'other', label: 'Other', color: 'bg-muted/40 text-muted-foreground border-border/40' },
                ].filter(m => (byMethod[m.key] || 0) > 0).map(m => (
                  <span key={m.key} className={`text-[11px] px-2 py-1 rounded-full border ${m.color}`}>
                    {m.label} · {fmt(byMethod[m.key])}
                  </span>
                ))}
              </div>
            </div>
          )}

          {checkouts === 0 && (
            <div className="mt-4 pt-3 border-t border-border/30 text-center">
              <p className="text-xs text-muted-foreground">No checkouts yet today. Revenue will appear here as patients check out.</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const Section = ({ title, hint, children, testId }) => (
  <section className="space-y-3" data-testid={testId}>
    <div className="flex items-end justify-between gap-3 border-b-2 border-primary/40 pb-2">
      <h3 className="text-base md:text-lg font-extrabold uppercase tracking-wide text-primary">{title}</h3>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
    {children}
  </section>
);

const StatTile = ({ label, value, icon: Icon, sub, subClass, testId, onClick }) => (
  <Card className={`gold-border bg-card/60 ${onClick ? 'cursor-pointer hover:bg-primary/5 transition-colors' : ''}`} onClick={onClick} data-testid={testId}>
    <CardContent className="p-4 flex items-center justify-between">
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className="text-3xl font-bold text-primary mt-1" data-testid={testId ? `${testId}-count` : undefined}>{value}</p>
        {sub && <p className={`text-[11px] mt-0.5 ${subClass || 'text-muted-foreground'}`} data-testid={testId ? `${testId}-expiring` : undefined}>{sub}</p>}
      </div>
      <Icon className="w-8 h-8 text-primary/40" />
    </CardContent>
  </Card>
);

export const Dashboard = ({ stats, onJump }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const tiles = [
    { id: 'new-patient', icon: UserPlus, label: 'New Patient Intake', desc: 'Register a new patient and create their record', color: 'amber', action: () => onJump('patients', 'new') },
    { id: 'search-patients', icon: Search, label: 'Search Patients', desc: 'Find by name, DOB, or phone', color: 'amber', action: () => onJump('patients') },
    { id: 'document-visit', icon: FileText, label: 'Document Visit', desc: 'Record an IV therapy session', color: 'amber', action: () => onJump('visits', 'new') },
    { id: 'followups', icon: Bell, label: 'Follow-Up Calls', desc: 'Complete next-day follow-up tasks', color: 'rose', action: () => onJump('followups') },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="bg-gradient-to-r from-primary/15 via-amber-500/5 to-transparent border border-primary/30 rounded-xl px-6 py-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold gold-gradient-text brand-heading">Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!</h2>
          <p className="text-muted-foreground text-sm mt-0.5">{today}</p>
        </div>
        <button type="button" onClick={() => onJump('calendar')} className="text-xs font-semibold uppercase tracking-wider text-primary border-2 border-primary/50 rounded-lg px-4 py-2 hover:bg-primary/10" data-testid="dashboard-open-calendar">Open Calendar →</button>
      </div>

      <Section title="Today at a glance" testId="dashboard-glance">
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${stats.membership ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4`}>
          <StatTile label="Total Patients" value={stats.total_patients ?? 0} icon={Users} onClick={() => onJump('patients')} />
          <StatTile label="Visits Today" value={stats.visits_today ?? 0} icon={FileText} onClick={() => onJump('calendar')} />
          <StatTile label="Pending Follow-Ups" value={stats.pending_followups ?? 0} icon={Bell} onClick={() => onJump('followups')} />
          {stats.membership && <StatTile label="Elite Members" value={stats.membership.active_members} icon={Crown} testId="members-tile"
            sub={`${stats.membership.credits_expiring_soon} credit${stats.membership.credits_expiring_soon === 1 ? '' : 's'} expiring this month`}
            subClass={stats.membership.credits_expiring_soon ? 'text-amber-300' : 'text-muted-foreground'} />}
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <Section title="Today's revenue" testId="dashboard-revenue">
            <RevenueSnapshot revenue={stats.revenue_today} onJump={onJump} />
          </Section>
        </div>
        <div className="lg:col-span-2">
          <Section title="Quick actions" testId="dashboard-actions">
            <div className="grid grid-cols-1 gap-3">
              {tiles.map(t => (
                <button key={t.id} type="button" onClick={t.action} data-testid={`quick-${t.id}`}
                  className={`text-left p-4 rounded-xl border-2 transition-all hover:scale-[1.01] hover:shadow-lg ${t.color === 'rose' ? 'border-rose-500/30 bg-rose-500/5 hover:bg-rose-500/10' : 'border-primary/30 bg-primary/5 hover:bg-primary/10'}`}>
                  <div className="flex items-center gap-3">
                    <t.icon className={`w-5 h-5 ${t.color === 'rose' ? 'text-rose-400' : 'text-primary'}`} />
                    <div>
                      <p className={`font-semibold ${t.color === 'rose' ? 'text-rose-400' : 'text-primary'}`}>{t.label}</p>
                      <p className="text-xs text-muted-foreground">{t.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </Section>
        </div>
      </div>

      {stats.membership && (
        <Section title="Elite memberships" hint="$450 · 3-month plan" testId="dashboard-memberships">
          <div className={`grid grid-cols-1 ${isAdmin ? 'xl:grid-cols-2' : ''} gap-4`}>
            <MembersPanel data={stats.membership} canRemind={isAdmin} onOpenPatient={(pid) => onJump('patients', { patientId: pid })} />
            {isAdmin && <MembershipRevenueReport />}
          </div>
        </Section>
      )}

      {isAdmin && (
        <Section title="Patient communications" testId="dashboard-communications">
          <SmsDelivery />
        </Section>
      )}
    </div>
  );
};

// ==================== VISIT (TREATMENT) MANAGEMENT ====================
